/**
 * Konusma ve mesaj API'si.
 *
 * ## Sunucu gecmisin SAHIBI
 *
 * Onceki halde tum sohbet deposu tarayici RAM'indeki tek bir degiskendi
 * (`let history = [SYSTEM]`) ve sayfa yenilenince %100 kayipti. Burada hicbir
 * yerel "gecmis" kopyasi tutulmuyor: liste ve mesajlar her zaman sunucudan
 * geliyor. Ikinci bir dogruluk kaynagi, iki kaynagin ayrisip sessizce yanlis
 * gecmis gostermesi demek olurdu.
 *
 * ## Sahiplik: 404, 403 DEGIL
 *
 * Baskasina ait bir konusma icin sunucu 404 donduruyor - varligi ASLA
 * dogrulamiyoruz. Arayuz de bu yuzden 404'u "bulunamadi" olarak gosteriyor.
 */

import { apiJson, apiVoid } from './api'
import type { UploadedAttachment } from './attachments'

export interface Conversation {
  id: string
  title: string
  title_source: string
  message_count: number
  pinned: boolean
  archived: boolean
  system_prompt: string | null
  preferred_model: string | null
  think_default: string | null
  last_message_at: string | null
  created_at: string
  copied_from_id: string | null
  /** Bagli oldugu proje (API ile gelen platform istekleri); siradan sohbette null. */
  project_id?: string | null
  /**
   * Gecici sohbet mi.
   *
   * Sunucudan GELIYOR, yerelde tutulmuyor: sayfa yenilendiginde de,
   * baglantiyla dogrudan acildiginda da ust seritteki uyarinin ve "Kapat"
   * dugmesinin gorunmesi gerekiyor. Yerel bir bayrak olsaydi yenilemeden
   * sonra kullanici gecici bir sohbette oldugunu BILMEDEN yazmaya devam
   * ederdi.
   */
  temporary?: boolean
}

/** `GET /api/projects` - sohbetleri gruplayan proje. */
export interface Project {
  id: string
  name: string
  conversation_count: number
  last_activity_at: string | null
  created_at: string
}

export function listProjects(): Promise<{ items: Project[] }> {
  return apiJson<{ items: Project[] }>('/api/projects')
}

/**
 * Proje icinde yeni sohbet acildiginda kenar cubugu sayilari ve acik projenin
 * listesini tazelesin diye (siradan liste 15 sn'de bir kendini yeniliyor, ama
 * proje sohbetleri o listede yok).
 */
export const PROJECTS_CHANGED_EVENT = 'hedefai:projects-changed'

export function notifyProjectsChanged(): void {
  window.dispatchEvent(new Event(PROJECTS_CHANGED_EVENT))
}

/** Kenar cubugundaki "+": yeni proje. Ayni ad varsa 409. */
export function createProject(name: string): Promise<Project> {
  return apiJson<Project>('/api/projects', {
    method: 'POST',
    body: JSON.stringify({ name }),
  })
}

/**
 * Projeyi siler.
 *
 * `withConversations` iki farkli niyeti ayiriyor (kullanici karari
 * 26.09.2026): `false` yalnizca basligi kaldiriyor ve sohbetler projesiz
 * olarak siradan listeye duşuyor; `true` proje ve icindeki sohbetleri
 * KALICI siliyor - geri donusu yok.
 */
export function deleteProject(
  projectId: string,
  withConversations: boolean,
): Promise<{ id: string; name: string; deleted_conversations: number }> {
  const query = withConversations ? '?with_conversations=true' : ''
  return apiJson(`/api/projects/${projectId}${query}`, { method: 'DELETE' })
}

/** Sohbeti projeye tasir; `null` projeden cikarir (siradan listeye doner). */
export function moveConversation(
  conversationId: string,
  projectId: string | null,
): Promise<Conversation> {
  return apiJson<Conversation>(`/api/conversations/${conversationId}`, {
    method: 'PATCH',
    body: JSON.stringify({ project_id: projectId ?? '' }),
  })
}

/** Bir projenin sohbetleri (siradan liste bunlari icermiyor). */
export function listProjectConversations(
  projectId: string,
  cursor?: string | null,
): Promise<ConversationPage> {
  const query = new URLSearchParams({ project_id: projectId })
  if (cursor) query.set('before', cursor)
  return apiJson<ConversationPage>(`/api/conversations?${query}`)
}

export interface MessageStats {
  input_tokens: number | null
  output_tokens: number | null
  seconds: number | null
  tokens_per_sec: number | null
}

export interface Message {
  id: string
  seq: number
  role: 'user' | 'assistant' | 'system'
  content: string
  think_text: string | null
  tool_cards: Record<string, unknown>[]
  status: 'complete' | 'streaming' | 'failed' | 'cancelled' | 'interrupted'
  error_code: string | null
  model: string | null
  think: string
  stats: MessageStats | null
  parent_message_id: string | null
  edited_from_id: string | null
  variant_index: number
  variant_count: number
  /**
   * Cagiranin bu mesaja verdigi oy.
   *
   * Mesajla BIRLIKTE geliyor: ayri bir cagri N+1 olurdu ve daha onemlisi
   * sayfa yenilendiginde oy KAYBOLMUS gorunurdu - kullanici da oyunun
   * kaydedilmedigini dusunurdu.
   */
  feedback: { rating: 'up' | 'down'; reason: string | null } | null
  created_at: string
  /** Yanitin BITTIGI an - saat etiketi ve toplam sure bundan. */
  completed_at?: string | null
  /** Ilk metin parcasinin ekrana dustugu an (kuyruk + dusunme suresi dahil). */
  first_token_at?: string | null
  /** Bu mesaja bagli ekler. Base64 YOK - yalnizca URL'ler. */
  attachments: UploadedAttachment[]
}

export interface ConversationPage {
  items: Conversation[]
  /** `null` ise son sayfa. */
  next_cursor: string | null
}

export interface MessageList {
  conversation: Conversation
  items: Message[]
}

export function listConversations(cursor?: string | null): Promise<ConversationPage> {
  // Keyset pagination: `before=<last_message_at>`. Offset DEGIL - es zamanli
  // insert'lerde offset kayiyor ve kullanici ayni konusmayi iki kez veya
  // hic gormuyor.
  const query = cursor ? `?before=${encodeURIComponent(cursor)}` : ''
  return apiJson<ConversationPage>(`/api/conversations${query}`)
}

/**
 * Baslik aramasi - TUM sohbetlerde, sunucuda. Yerel filtre yalnizca yuklenmis
 * ilk sayfayi gorurdu; Turkce katlama ("ı"/"İ") da sunucuda FTS ile ayni kural.
 */
export function searchConversationTitles(query: string): Promise<ConversationPage> {
  return apiJson<ConversationPage>(`/api/conversations?q=${encodeURIComponent(query)}`)
}

export interface MessageSearchHit {
  message_id: string
  conversation_id: string
  conversation_title: string
  role: string
  snippet: string
}

/** Mesaj icerigi aramasi (FTS5). */
export function searchMessages(query: string): Promise<{ items: MessageSearchHit[] }> {
  return apiJson<{ items: MessageSearchHit[] }>(`/api/search?q=${encodeURIComponent(query)}`)
}

export function getMessages(conversationId: string): Promise<MessageList> {
  return apiJson<MessageList>(`/api/conversations/${conversationId}/messages`)
}

export function renameConversation(conversationId: string, title: string): Promise<Conversation> {
  // `title` set etmek sunucuda `title_source='user'` yapiyor ve otomatik
  // baslıklandirma bir daha ASLA ezmiyor.
  return apiJson<Conversation>(`/api/conversations/${conversationId}`, {
    method: 'PATCH',
    body: JSON.stringify({ title }),
  })
}

export function deleteConversation(conversationId: string): Promise<void> {
  // Soft delete: `?hard=true` olmadan geri alinabilir.
  return apiVoid(`/api/conversations/${conversationId}`, { method: 'DELETE' })
}

/**
 * Kullanicinin ACIK gecici sohbeti; yoksa `null`.
 *
 * Gecici sohbet gecmis listesinde YOK, bu yuzden kenar cubugundaki ozel
 * satir bunu ayrica soruyor - kullanicinin ona geri donup kapatabilmesinin
 * tek yolu o satir.
 */
export function getTemporaryConversation(): Promise<Conversation | null> {
  return apiJson<{ conversation: Conversation | null }>('/api/conversations/temporary').then(
    (page) => page.conversation,
  )
}

/**
 * Gecici sohbeti KAPATIR: icerik sunucuda siliniyor.
 *
 * Siradan silmeden ayri bir isim tasimasi bilincli - cagiranin ne yaptigini
 * bilmesi gerekiyor. Sunucu tarafinda ayni uc, ama gecici sohbette soft
 * delete YETMIYOR (bkz. `services/temporary.py`).
 */
export function closeTemporaryConversation(conversationId: string): Promise<void> {
  return apiVoid(`/api/conversations/${conversationId}`, { method: 'DELETE' })
}

/** Gecici sohbet acilip kapandiginda kenar cubugu satirini tazelesin diye. */
export const TEMPORARY_CHANGED_EVENT = 'hedefai:temporary-changed'

export function notifyTemporaryChanged(): void {
  window.dispatchEvent(new Event(TEMPORARY_CHANGED_EVENT))
}

// --------------------------------------------------------------------- run

export interface RunInfo {
  id: string
  conversation_id: string
  assistant_message_id: string
  status: 'queued' | 'running' | 'done' | 'failed' | 'cancelled' | 'interrupted'
  model: string | null
  think: string
  chunk_count: number
  cancel_requested: boolean
  error_code: string | null
  queue_position?: number | null
  eta_seconds?: number | null
}

/**
 * Cagiranin bekleyen/calisan isleri.
 *
 * SPA acilista bunu cagiriyor. Bu rota olmadan sayfa yenilendikten sonra
 * hangi run'a yeniden baglanacagi BILINEMEZ - run id yalnizca akisin
 * icinde geliyordu.
 */
export function activeRuns(): Promise<{ items: RunInfo[] }> {
  return apiJson<{ items: RunInfo[] }>('/api/runs/active')
}

export function cancelRun(runId: string): Promise<unknown> {
  return apiJson<unknown>(`/api/runs/${runId}/cancel`, { method: 'POST' })
}

export interface SubmitResult {
  conversation: Conversation
  user_message: Message
  assistant_message: Message
  run: { id: string; status: string; queue_position: number; estimated_wait_seconds: number | null }
}

/**
 * Mesaj gonderir. **202** + run id donuyor, akis DEGIL.
 *
 * Uretim ile bu HTTP istegi arasinda hicbir bag yok: token'lar ayri bir
 * `GET /api/runs/{id}/stream?after=` uzerinden geliyor ve o akis her an
 * kopabilir, yeniden baglanabilir, hic acilmayabilir.
 *
 * `Accept: application/json` artik GEREKMIYOR ama duruyor: Faz 8'e kadar
 * sunucu, bu baslik yoksa senkron bir SSE facade'i donduruyordu (tek
 * tuketicisi eski vanilla arayuzdu). Facade kaldirildi - uc her `Accept`
 * degeri icin 202 donuyor. Baslik yine de dogru ve niyeti acikca
 * soyluyor, o yuzden kaldirilmadi.
 */
export function submitMessage(input: {
  conversationId: string | null
  /** Yeni sohbet bu projenin altinda acilsin (yalnizca `conversationId` null iken). */
  projectId?: string | null
  content: string
  attachmentIds?: string[]
  clientMessageId: string
  /** `null`/verilmemis = konusma/kullanici varsayilanina birak (`auto`). */
  model?: string | null
  /** Gorsel uretme secenekleri; `null` = otomatik boyut / hizli kalite. */
  imageSize?: string | null
  imageQuality?: string | null
  /** Dusunme modu; verilmezse sunucu varsayilani. Sesli gorusme `off` gonderiyor. */
  think?: string
  /** Yanit seslendirilecek: kisa, markdown'siz, aractan once tek cumle. */
  voice?: boolean
  /** Yeni sohbet GECICI acilsin (yalnizca `conversationId` null iken). */
  temporary?: boolean
}): Promise<SubmitResult> {
  return apiJson<SubmitResult>('/api/chat', {
    method: 'POST',
    headers: { Accept: 'application/json' },
    body: JSON.stringify({
      conversation_id: input.conversationId,
      ...(input.conversationId === null && input.projectId
        ? { project_id: input.projectId }
        : {}),
      content: input.content,
      attachment_ids: input.attachmentIds ?? [],
      client_message_id: input.clientMessageId,
      model: input.model ?? null,
      image_size: input.imageSize ?? null,
      image_quality: input.imageQuality ?? null,
      ...(input.think ? { think: input.think } : {}),
      ...(input.voice ? { voice: true } : {}),
      ...(input.conversationId === null && input.temporary ? { temporary: true } : {}),
    }),
  })
}

/**
 * Yarida kesilmis bir yaniti KALDIGI YERDEN surdurur.
 *
 * `regenerate`den farki: yeni bir asistan dali acmiyor, AYNI mesaji yeniden
 * acip metnin devamini yaziyor. Sunucu kismi metni akisa geri veriyor, yani
 * arayuz icin akis normal bir uretimden farksiz.
 */
export function continueAnswer(
  conversationId: string,
  assistantMessageId: string,
): Promise<SubmitResult> {
  return apiJson<SubmitResult>('/api/chat', {
    method: 'POST',
    headers: { Accept: 'application/json' },
    body: JSON.stringify({
      conversation_id: conversationId,
      content: '',
      continue_message_id: assistantMessageId,
    }),
  })
}

export interface RegenerateResult {
  run: SubmitResult['run']
  assistant_message_id: string
  conversation_id: string
}

/**
 * Yaniti yeniden uretir: ayni kullanici mesajinin altinda YENI asistan dali.
 *
 * Eski yanit SILINMIYOR - varyant olarak duruyor. `POST /api/chat`ten farki
 * yeni bir KULLANICI mesaji yaratmamasi.
 */
export function regenerate(
  conversationId: string,
  userMessageId: string,
): Promise<RegenerateResult> {
  return apiJson<RegenerateResult>(`/api/conversations/${conversationId}/runs`, {
    method: 'POST',
    body: JSON.stringify({ regenerate_after_message_id: userMessageId }),
  })
}

/**
 * Varyant dalina gecer.
 *
 * Mesaj duzenleme ve yeniden uretme YENI kardesler yaratiyor; eski dal
 * SILINMIYOR. Bu cagri `conversations.active_leaf_id`i tasiyip o dalin
 * sonuna gidiyor - yani `< 2/3 >` gezinmesi veri kaybetmeden calisiyor.
 *
 * `delta` verilirse hedef kardesi SUNUCU buluyor. Istemcide bulmak, agaci
 * istemcide yeniden yorumlamak demek olurdu - ikinci bir dogruluk kaynagi.
 */
export function switchVariant(
  conversationId: string,
  messageId: string,
  delta?: -1 | 1,
): Promise<{ active_leaf_id: string | null; message_id: string }> {
  return apiJson(`/api/conversations/${conversationId}/switch-variant`, {
    method: 'POST',
    body: JSON.stringify({ message_id: messageId, ...(delta ? { delta } : {}) }),
  })
}
